import React, { useState, useEffect } from "react";
import { getTimeZoneLabel } from "../../../shared/utils/timeZone";
import { btn } from "../../../shared/styles/buttonStyles";
import axios from "axios";
import { getApiErrorMessage } from "../../../shared/utils/apiError";
import { useMsal } from "@azure/msal-react";
import { useLoading } from "../../../shared/state/LoadingContext";
import { toast } from "react-toastify";
import ActivityLogTable, { ActivityLogTableItem } from "../../../shared/ui/ActivityLogTable";
import FeatureInformationPanel from "../../../shared/ui/FeatureInformationPanel";
import FeatureTabs, { FeatureTab } from "../../../shared/ui/FeatureTabs";
import ResourceContextPanel from "../../../shared/ui/ResourceContextPanel";
import ScheduleAutomationPanel from "../../../shared/ui/ScheduleAutomationPanel";
import { PaginatedResponse } from "../../../shared/types/pagination";
import {
  ActivityFeedEntry,
  ACTIVITY_API_PAGE_SIZE,
  ACTIVITY_LOG_PAGE_SIZE,
  ACTIVITY_LOG_POLL_INTERVAL_MS,
  formatActivityValue,
  hasActivityValueChanged,
  isExecutionActive,
  mapExecutionFeedEntries,
  parseActivityDetailsJson,
  sortActivityFeedEntries,
} from "../../../shared/utils/activityFeed";
import { formatAuditDate, formatAuditTime24Hour } from "../../../shared/utils/formatAuditTime24Hour";
import { formatActivityDuration } from "../../../shared/utils/formatActivityDuration";
import formatTimeTo12Hour from "../../../shared/utils/formatTimeTo12Hour";
import { getAccessToken } from "../../../shared/auth/getAccessToken";

// ── Type Definitions ──────────────────────────────────────────────────────────

interface PipelineInfo {
  partitionKey: string;
  rowKey: string;
  name: string;
  timestamp: string;
  resourceGroup: string;
  branch: string;
  subscription: string;
  parameters: { [key: string]: string };
  organization: string;
  project: string;
  pipelineId: string;
  scheduledTime: string | null;
  isScheduled: boolean;

  isTurnOnScheduled: boolean;
  turnOnScheduledTime: string | null;
  isTurnOffScheduled: boolean;
  turnOffScheduledTime: string | null;
  scheduleDays: string[];

  isOnline: boolean;
  vmssName: string | null;
}

interface ActivityLogEntry {
  id: string;
  actionType: string;
  status: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  durationMs?: number | null;
  changeSummary: string;
  changesJson?: string;
  errorMessage?: string;
  pipelineRunUrl?: string;
  targetResourceGroup?: string;
  targetResourceName?: string;
}

interface ExecutionEntry {
  id: string;
  executionId: string;
  actionType: string;
  executionType: string;
  triggerType: string;
  status: string;
  statusMessage?: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  runningAtUtc?: string | null;
  updatedAtUtc?: string;
  completedAtUtc?: string;
  durationMs?: number | null;
  pipelineRunId?: string;
  pipelineRunUrl?: string;
  errorMessage?: string;
  metadataJson?: string;
  targetResourceGroup?: string;
  targetResourceName?: string;
}

type PipelineLoadingAction = "turn_on" | "turn_off" | "schedule" | null;

// ── Constants & Utilities ─────────────────────────────────────────────────────

const DEFAULT_SCHEDULE_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
function resolveScheduleDays(scheduleDays?: string[] | null): string[] {
  if (Array.isArray(scheduleDays)) {
    return scheduleDays;
  }
  // Keep empty/null as empty so UI does not silently reselect weekdays.
  return [];
}

// Product to keyword aliases used to split legacy /dev/configure pipelines into product pages.
const PRODUCT_KEYWORD_MAP: Record<string, string[]> = {
  accelerus: ["accelerus"],
  xuno: ["xuno"],
  bpos: ["bpos"],
  gradexpert: ["gradexpert", "gradexpertdatatransfers", "gx"],
};

function normalizeMatchValue(value: string | null | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function matchesProduct(pipeline: PipelineInfo, product: string): boolean {
  const normalizedProduct = normalizeMatchValue(product);
  const keywords =
    PRODUCT_KEYWORD_MAP[normalizedProduct] ?? [normalizedProduct];

  const searchableFields = [
    pipeline.partitionKey,
    pipeline.project,
    pipeline.name,
    pipeline.resourceGroup,
    pipeline.vmssName,
    pipeline.rowKey,
    ...Object.values(pipeline.parameters ?? {}),
  ]
    .map(normalizeMatchValue)
    .filter(Boolean);

  return keywords.some((keyword) =>
    searchableFields.some((field) => field.includes(keyword))
  );
}

// Xuno product names, otherwise same fallback as Prestia
function getResourceName(pipeline: PipelineInfo, product: string): string {
  if (product === "gradexpert") {
    return "GradeXpert Web";
  }
  if (product === "accelerus" && pipeline.vmssName) {
    return "Accelerus Dev Worker Role (VMSS)";
  }
  return pipeline.vmssName || pipeline.name || `Pipeline ${pipeline.pipelineId}`;
}

function buildAzureDevOpsPipelineUrl(pipeline: PipelineInfo): string | null {
  const organization = pipeline.organization?.trim();
  const project = pipeline.project?.trim();
  const pipelineId = pipeline.pipelineId?.trim();

  if (!organization || !project || !pipelineId) {
    return null;
  }

  return `https://dev.azure.com/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/_build?definitionId=${encodeURIComponent(pipelineId)}`;
}

function buildAzureResourceGroupUrl(pipeline: PipelineInfo): string | null {
  const subscription = pipeline.subscription?.trim();
  const resourceGroup = pipeline.resourceGroup?.trim();

  if (!subscription || !resourceGroup) {
    return null;
  }

  return `https://portal.azure.com/#resource/subscriptions/${encodeURIComponent(subscription)}/resourceGroups/${encodeURIComponent(resourceGroup)}/overview`;
}

// ── Child Component: PipelineActions ──────────────────────────────────────────

// Renders manual turn on/off buttons and schedule automation panel for each pipeline
const PipelineActions: React.FC<{
  pipeline: PipelineInfo;
  loadingAction: PipelineLoadingAction;
  turnOff: (partitionKey: string, rowKey: string, pipelineName: string) => void;
  runPipeline: (partitionKey: string, rowKey: string, pipelineName: string) => void;
  updateSchedule: (
    partitionKey: string,
    rowKey: string,
    isTurnOnScheduled: boolean,
    turnOnScheduledTime?: string,
    isTurnOffScheduled?: boolean,
    turnOffScheduledTime?: string,
    scheduleDays?: string[]
  ) => void;
}> = ({ pipeline, loadingAction, turnOff, runPipeline, updateSchedule }) => {
  const [turnOnScheduledTime, setTurnOnScheduledTime] = useState(
    pipeline.turnOnScheduledTime || ""
  );
  const [turnOffScheduledTime, setTurnOffScheduledTime] = useState(
    pipeline.turnOffScheduledTime || ""
  );
  const [isScheduleEnabled, setIsScheduleEnabled] = useState(
    pipeline.isTurnOnScheduled || pipeline.isTurnOffScheduled
  );
  const [scheduleDays, setScheduleDays] = useState<string[]>(
    resolveScheduleDays(pipeline.scheduleDays)
  );

  useEffect(() => {
    setTurnOnScheduledTime(pipeline.turnOnScheduledTime || "");
    setTurnOffScheduledTime(pipeline.turnOffScheduledTime || "");
    setIsScheduleEnabled(pipeline.isTurnOnScheduled || pipeline.isTurnOffScheduled);
    setScheduleDays(resolveScheduleDays(pipeline.scheduleDays));
  }, [
    pipeline.turnOnScheduledTime,
    pipeline.isTurnOnScheduled,
    pipeline.isTurnOffScheduled,
    pipeline.turnOffScheduledTime,
    pipeline.scheduleDays,
  ]);

  const handleDayToggle = (day: string) => {
    setScheduleDays((prev) =>
      prev.includes(day)
        ? prev.filter((currentDay) => currentDay !== day)
        : [...prev, day]
    );
  };

  const effectiveScheduleDays = isScheduleEnabled ? scheduleDays : [];

  const handleScheduleEnabledChange = (enabled: boolean) => {
    setIsScheduleEnabled(enabled);
    if (!enabled) {
      // Keep UI and payload consistent: disabling schedule clears selected days.
      setScheduleDays([]);
      return;
    }

    // Re-enable with a sensible default only when user currently has no selected days.
    setScheduleDays((prev) => (prev.length > 0 ? prev : DEFAULT_SCHEDULE_DAYS));
  };

  const isTurnOffLoading = loadingAction === "turn_off";
  const isTurnOnLoading = loadingAction === "turn_on";
  const isScheduleLoading = loadingAction === "schedule";

  return (
    <div className="workspace-card flex flex-col overflow-hidden border border-slate-200 bg-white">
      <div className="order-2 border-t border-slate-200 px-5 py-4">
        <div className="mb-2">
          <h4 className="text-sm font-bold leading-5 text-slate-800">
            Manual Action
          </h4>
          <p className="mt-1 text-[11px] leading-4 text-slate-500">
            Run on/off now.
          </p>
        </div>
        <hr className="my-3 border-slate-200" />
        <div className="flex flex-wrap justify-start gap-2">
        <button
          type="button"
          onClick={() => turnOff(pipeline.partitionKey, pipeline.rowKey, pipeline.name)}
          disabled={loadingAction !== null}
          className={btn.destructiveSmall}
        >
          {isTurnOffLoading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Running...
            </>
          ) : (
            "Turn Off Service"
          )}
        </button>
        <button
          type="button"
          onClick={() => runPipeline(pipeline.partitionKey, pipeline.rowKey, pipeline.name)}
          disabled={loadingAction !== null}
          className={btn.positiveSmall}
        >
          {isTurnOnLoading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Running...
            </>
          ) : (
            "Turn On Service"
          )}
        </button>
        </div>
      </div>
      <form
        className="order-1"
        onSubmit={(e) => {
          e.preventDefault();
          const formattedTurnOnTime = formatTimeTo12Hour(turnOnScheduledTime);
          const formattedTurnOffTime = formatTimeTo12Hour(turnOffScheduledTime);
          updateSchedule(
            pipeline.partitionKey,
            pipeline.rowKey,
            isScheduleEnabled && !!formattedTurnOnTime,
            formattedTurnOnTime || undefined,
            isScheduleEnabled && !!formattedTurnOffTime,
            formattedTurnOffTime || undefined,
            effectiveScheduleDays
          );
        }}
      >
        <ScheduleAutomationPanel
          className="border-0 rounded-none p-5"
          title="Schedule Automation"
          description="When enabled, the selected days run automatically at the specified time."
          isEnabled={isScheduleEnabled}
          onEnabledChange={handleScheduleEnabledChange}
          scheduleDays={effectiveScheduleDays}
          onDayToggle={handleDayToggle}
          turnOnTimeLabel={`Turn On Time (${getTimeZoneLabel()})`}
          turnOffTimeLabel={`Turn Off Time (${getTimeZoneLabel()})`}
          turnOnScheduledTime={turnOnScheduledTime}
          turnOffScheduledTime={turnOffScheduledTime}
          onTurnOnScheduledTimeChange={setTurnOnScheduledTime}
          onTurnOffScheduledTimeChange={setTurnOffScheduledTime}
          submitContent={
            isScheduleLoading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Running...
              </>
            ) : (
              "Apply Schedule"
            )
          }
          submitButtonType="submit"
          disabled={loadingAction !== null}
        />
      </form>
    </div>
  );
};

// ── Main Component: PowerOnOff ────────────────────────────────────────────────

interface PowerOnOffProps {
  product: string;
  rowKeyFilter?: string;
}

const PowerOnOff: React.FC<PowerOnOffProps> = ({
  product,
  rowKeyFilter,
}) => {
  // ── State Management
  const { instance, accounts } = useMsal();
  const { setLoading } = useLoading();
  const [allPipelines, setAllPipelines] = useState<PipelineInfo[]>([]);
  const [loadingPipelines, setLoadingPipelines] = useState<Record<string, PipelineLoadingAction>>({});
  const [activityFeedEntries, setActivityFeedEntries] = useState<ActivityFeedEntry[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsPage, setActivityLogsPage] = useState(1);
  const [activityLogsTotalPages, setActivityLogsTotalPages] = useState(1);
  const [activityLogsTotalCount, setActivityLogsTotalCount] = useState(0);
  const [hasActiveExecutions, setHasActiveExecutions] = useState(false);
  const [activeTab, setActiveTab] = useState<FeatureTab>("operations");

  const apiUrl = process.env.REACT_APP_API_URL;
  const isLocalPreview =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

  // Filter pipelines relevant to this product, with optional exact row-key targeting
  const pipelines = allPipelines
    .filter((p) => matchesProduct(p, product))
    .filter((p) =>
      rowKeyFilter ? p.rowKey.toLowerCase() === rowKeyFilter.toLowerCase() : true
    );
  const previewPipeline: PipelineInfo = {
    partitionKey: `${product}|development`,
    rowKey: `${product}-preview`,
    name: `${productLabelFromKey(product)} Development Pipeline`,
    timestamp: "",
    resourceGroup: `${product.toLowerCase()}-dev-rg`,
    branch: "development",
    subscription: "Preview only",
    parameters: {},
    organization: "semaphore-consulting",
    project: productLabelFromKey(product),
    pipelineId: "Preview",
    scheduledTime: null,
    isScheduled: false,
    isTurnOnScheduled: true,
    turnOnScheduledTime: "08:00",
    isTurnOffScheduled: true,
    turnOffScheduledTime: "18:00",
    scheduleDays: DEFAULT_SCHEDULE_DAYS,
    isOnline: false,
    vmssName: null,
  };

  // ── Effects: Data Fetching & Polling
  useEffect(() => {
    if (instance && accounts.length > 0) {
      fetchPipelines();
      fetchActivityLogs(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts, product]);

  useEffect(() => {
    if (!hasActiveExecutions) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchPipelines(false, false);
      void fetchActivityLogs(activityLogsPage, false, false);
    }, ACTIVITY_LOG_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveExecutions, activityLogsPage, product]);

  // ── Helper Functions: Change Analysis & Formatting
  // Xuno writes ChangesJson as { field: { before, after } }, not Prestia's fields array
  const extractChangeLines = (changesJson?: string) => {
    const parsed = parseActivityDetailsJson<any>(changesJson);
    if (!parsed) return [] as string[];

    const labels: Record<string, string> = {
      turnOnEnabled: "Turn On Enabled",
      turnOnTime: "Turn On Time",
      turnOffEnabled: "Turn Off Enabled",
      turnOffTime: "Turn Off Time",
      scheduleDays: "Schedule Days",
    };
    const lines: string[] = [];
    Object.entries(labels).forEach(([key, label]) => {
      const change = parsed?.[key];
      if (change && hasActivityValueChanged(change.before, change.after)) {
        lines.push(`${label}: ${formatActivityValue(change.before)} -> ${formatActivityValue(change.after)}`);
      }
    });
    const deletedResources = Array.isArray(parsed?.deletedResources) ? parsed.deletedResources : [];
    deletedResources.forEach((resource: any) => {
      lines.push(`Resource: ${formatActivityValue(resource.resourceType)} / ${formatActivityValue(resource.resourceName)}`);
    });

    return lines.slice(0, 12);
  };

  const getActivityChangeLines = (log: ActivityFeedEntry) => {
    const parsedDetails = parseActivityDetailsJson<any>(log.detailsJson);
    const lines: string[] = [];
    const serviceStatusBefore = parsedDetails?.serviceStatus?.before
      ? String(parsedDetails.serviceStatus.before).toLowerCase()
      : null;
    const rawServiceStatusAfter = parsedDetails?.serviceStatus?.after
      ? String(parsedDetails.serviceStatus.after).toLowerCase()
      : null;
    // Turn On is pipeline-async: backend never writes serviceStatus.after, so infer "online" from the action type.
    const serviceStatusAfter = rawServiceStatusAfter
      ?? (log.actionType === "turn_on" && serviceStatusBefore ? "online" : null);

    if (log.source === "execution") {
      if (serviceStatusBefore && serviceStatusAfter && hasActivityValueChanged(serviceStatusBefore, serviceStatusAfter)) {
        lines.push(`Service Status: ${serviceStatusBefore} -> ${serviceStatusAfter}`);
      }

      return lines.slice(0, 6);
    }

    if (serviceStatusBefore && serviceStatusAfter && hasActivityValueChanged(serviceStatusBefore, serviceStatusAfter)) {
      return [`Service Status: ${serviceStatusBefore} -> ${serviceStatusAfter}`];
    }

    return extractChangeLines(log.detailsJson);
  };

  const buildActivityFeed = (
    executionItems: ExecutionEntry[],
    auditItems: ActivityLogEntry[]
  ): ActivityFeedEntry[] => {
    const executionFeed = mapExecutionFeedEntries(executionItems);

    const auditFeed: ActivityFeedEntry[] = auditItems
      .filter((log) => log.actionType === "update_schedule")
      .map((log) => ({
        id: log.id,
        source: "audit" as const,
        actionType: log.actionType,
        status: log.status,
        actorName: log.actorName,
        actorEmail: log.actorEmail,
        startedAtUtc: log.startedAtUtc,
        updatedAtUtc: undefined,
        durationMs: log.durationMs,
        detailsJson: log.changesJson,
        errorMessage: log.errorMessage,
        pipelineRunUrl: undefined,
        targetResourceGroup: log.targetResourceGroup,
        targetResourceName: log.targetResourceName,
      }));

    return sortActivityFeedEntries([...executionFeed, ...auditFeed]);
  };

  // ── API Functions: Data Fetching
  const fetchPipelines = async (showErrorToast = true, showGlobalLoading = true) => {
    try {
      if (showGlobalLoading) {
        setLoading(true);
      }
      const accessToken = await getAccessToken();

      const response = await axios.get(`${apiUrl}/ManageApps`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      setAllPipelines(response.data);
    } catch (error) {
      console.error("Error fetching pipelines:", error);
      if (showErrorToast) {
        toast.error("Failed to fetch pipeline information");
      }
    } finally {
      if (showGlobalLoading) {
        setLoading(false);
      }
    }
  };

  const fetchActivityLogs = async (page = 1, showErrorToast = true, showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setActivityLogsLoading(true);
      }
      const accessToken = await getAccessToken();

      // GradeXpert staging/16022 each have their own log, picked by the pipeline row key
      const params = { pageSize: ACTIVITY_API_PAGE_SIZE, target: rowKeyFilter };
      const [auditItems, executionItems] = await Promise.all([
        axios.get<PaginatedResponse<ActivityLogEntry>>(`${apiUrl}/ManageApps/activity-logs/${product}`, {
          params,
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((response) => response.data.items),
        axios.get<PaginatedResponse<ExecutionEntry>>(`${apiUrl}/ManageApps/executions/${product}`, {
          params,
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((response) => response.data.items),
      ]);
      const mergedFeed = buildActivityFeed(executionItems, auditItems);
      const totalCount = mergedFeed.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / ACTIVITY_LOG_PAGE_SIZE));
      const safePage = Math.min(Math.max(page, 1), totalPages);
      const pagedItems = mergedFeed.slice(
        (safePage - 1) * ACTIVITY_LOG_PAGE_SIZE,
        safePage * ACTIVITY_LOG_PAGE_SIZE
      );

      setActivityFeedEntries(pagedItems);
      setActivityLogsPage(safePage);
      setActivityLogsTotalPages(totalPages);
      setActivityLogsTotalCount(totalCount);
      // Poll only while this page has a queued or running run
      setHasActiveExecutions(mergedFeed.some((item) => item.source === "execution" && isExecutionActive(item.status)));
    } catch (error) {
      // Failed poll keeps the last rows and keeps polling
      if (!showLoadingState) {
        return;
      }
      if (showErrorToast) {
        toast.error("Failed to load activity logs.");
      }
      setActivityFeedEntries([]);
      setActivityLogsPage(1);
      setActivityLogsTotalPages(1);
      setActivityLogsTotalCount(0);
      setHasActiveExecutions(false);
    } finally {
      if (showLoadingState) {
        setActivityLogsLoading(false);
      }
    }
  };

  // ── Action Handlers: Pipeline Operations
  const runPipeline = async (partitionKey: string, rowKey: string, pipelineName: string) => {
    try {
      setLoadingPipelines((prev) => ({ ...prev, [rowKey]: "turn_on" }));

      const accessToken = await getAccessToken();

      const response = await axios.post(
        `${apiUrl}/ManageApps/run/${rowKey}?partitionKey=${encodeURIComponent(partitionKey)}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setHasActiveExecutions(true);
        void fetchPipelines(false, false);
        void fetchActivityLogs(1, false, false);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error running pipeline:", error);
      toast.error(getApiErrorMessage(error, `Failed to run pipeline: ${pipelineName}.`));
    } finally {
      setLoadingPipelines((prev) => ({ ...prev, [rowKey]: null }));
    }
  };

  const turnoff = async (partitionKey: string, rowKey: string, pipelineName: string) => {
    try {
      setLoadingPipelines((prev) => ({ ...prev, [rowKey]: "turn_off" }));

      const accessToken = await getAccessToken();

      const response = await axios.post(
        `${apiUrl}/ManageApps/turnoff/${rowKey}?partitionKey=${encodeURIComponent(partitionKey)}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setHasActiveExecutions(true);
        void fetchActivityLogs(1, false, false);
        window.setTimeout(() => {
          void fetchPipelines(false, false);
        }, 1500);
      } else {
        toast.error(response.data.message);
      }
    } catch (error: any) {
      console.error("Error turnoff app:", error);
      if (error?.response?.status === 404) {
        // API returns 404 when the pipeline config row is missing, not when resources are gone
        toast.error(`No pipeline configuration found for ${pipelineName}.`);
      } else {
        toast.error(getApiErrorMessage(error, "Failed to turn off the service."));
      }
    } finally {
      setLoadingPipelines((prev) => ({ ...prev, [rowKey]: null }));
    }
  };

  const updateSchedule = async (
    partitionKey: string,
    rowKey: string,
    isTurnOnScheduled: boolean,
    turnOnScheduledTime?: string,
    isTurnOffScheduled?: boolean,
    turnOffScheduledTime?: string,
    scheduleDays?: string[]
  ) => {
    try {
      setLoadingPipelines((prev) => ({ ...prev, [rowKey]: "schedule" }));

      const accessToken = await getAccessToken();

      const response = await axios.put(
        `${apiUrl}/ManageApps/schedule/${rowKey}?partitionKey=${encodeURIComponent(partitionKey)}`,
        {
          isTurnOnScheduled,
          turnOnScheduledTime: turnOnScheduledTime ? turnOnScheduledTime : null,
          isTurnOffScheduled,
          turnOffScheduledTime: turnOffScheduledTime
            ? turnOffScheduledTime
            : null,
          scheduleDays: scheduleDays ?? [],
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        void fetchPipelines();
        void fetchActivityLogs(1);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error updating schedule:", error);
      toast.error(getApiErrorMessage(error, "Failed to apply schedule."));
    } finally {
      setLoadingPipelines((prev) => ({ ...prev, [rowKey]: null }));
    }
  };

  // ── Render: Data Mapping & UI
  const productLabel = productLabelFromKey(product);
  const activityLogRows: ActivityLogTableItem[] = activityFeedEntries.map((log) => ({
    id: log.id,
    dateText: formatAuditDate(log.startedAtUtc),
    timeText: formatAuditTime24Hour(log.startedAtUtc),
    durationText: formatActivityDuration(log.durationMs),
    runningAtUtc: log.runningAtUtc,
    actionText:
      log.actionType === "turn_on"
        ? "Turn On"
        : log.actionType === "turn_off"
          ? "Turn Off"
          : "Update Schedule",
    byName: log.actorName || "Unknown",
    byEmail: log.actorEmail,
    status: log.status,
    changeLines: getActivityChangeLines(log),
    errorMessage: log.errorMessage,
    pipelineRunUrl: log.pipelineRunUrl,
  }));

  return (
    <div className="feature-workspace">
      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "information" && (
        <FeatureInformationPanel summary={`Controls the ${productLabel} development service lifecycle and schedule.`}>
          <h3 className="text-sm font-semibold text-slate-800">Service lifecycle</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
          {product === "accelerus" ? (
            <>
              <li><span className="font-semibold">Turn On</span> deploys Accelerus Worker resources and takes 45-60 minutes.</li>
              <li><span className="font-semibold">Turn Off</span> removes the resources and takes 1-2 minutes.</li>
              <li>Use Schedule Automation to turn the environment on or off at set times.</li>
            </>
          ) : product === "bpos" ? (
            <>
              <li><span className="font-semibold">Turn On</span> deploys BPOS development resources and takes 10-15 minutes.</li>
              <li><span className="font-semibold">Turn Off</span> removes the resources and takes 1-2 minutes.</li>
              <li>Use Schedule Automation to turn the environment on or off at set times.</li>
            </>
          ) : product === "gradexpert" ? (
            <>
              <li><span className="font-semibold">Turn On</span> deploys GradeXpert resources and takes 5-10 minutes.</li>
              <li><span className="font-semibold">Turn Off</span> removes the resources and takes 1-2 minutes.</li>
              <li>Use Schedule Automation to turn the environment on or off at set times.</li>
            </>
          ) : (
            <>
              <li><span className="font-semibold">Turn On</span> deploys resources.</li>
              <li><span className="font-semibold">Turn Off</span> removes the resources and takes 1-2 minutes.</li>
              <li>Use Schedule Automation to manage the deployment schedule.</li>
            </>
          )}
          <li>Execution: Turn on: Azure DevOps pipeline. Turn off: Azure Resource Manager SDK.</li>
          </ul>
        </FeatureInformationPanel>
      )}

      {/* Hidden instead of unmounted so PipelineActions keeps unsaved schedule edits on tab switch */}
      <div id="feature-panel-operations" role="tabpanel" aria-labelledby="feature-tab-operations" tabIndex={0} hidden={activeTab !== "operations"}>
      <div className="mt-2 space-y-2">
        {pipelines.length === 0 ? (
          <div className="space-y-3">
            <div className="flex items-center justify-center rounded-sm border border-dashed border-gray-300 bg-white p-8 text-gray-500">
              <p>No pipelines configured for {productLabel}</p>
            </div>
            {isLocalPreview && (
              <div className="rounded-sm border border-dashed border-blue-300 bg-blue-50/60 p-3 shadow-sm">
                <div className="mb-2">
                  <h3 className="text-sm font-bold text-gray-900">Local Preview</h3>
                  <p className="text-[11px] text-slate-500">
                    This disabled sample card appears only on localhost so you can preview the schedule automation layout without configured pipeline data.
                  </p>
                </div>
                <div className="mt-3 rounded-sm border border-gray-200 bg-white p-4">
                  <ResourceContextPanel
                    resourceName={previewPipeline.name}
                    status="Preview"
                  />
                  <div className="mt-2 opacity-80">
                    <div className="flex flex-col gap-2">
                        <div className="order-2 workspace-subcard border border-slate-200 bg-white p-3">
                        <div className="mb-2">
                          <h4 className="text-sm font-bold leading-5 text-slate-800">Manual Action</h4>
                          <p className="mt-1 text-[11px] leading-4 text-slate-500">
                            Run turn on/off now.
                          </p>
                        </div>
                        <hr className="my-3 border-slate-200" />
                        <div className="flex flex-wrap justify-start gap-2">
                          <button type="button" disabled className={btn.destructiveSmall}>
                            Turn Off Service
                          </button>
                          <button type="button" disabled className={btn.positiveSmall}>
                            Turn On Service
                          </button>
                        </div>
                      </div>
                      <ScheduleAutomationPanel
                        className="order-1"
                        title="Schedule Automation"
                        description="Preview of the selected days and turn on/off schedule layout."
                        isEnabled
                        onEnabledChange={() => {
                          // Local preview is read-only, so this intentionally does nothing.
                        }}
                        scheduleDays={previewPipeline.scheduleDays}
                        onDayToggle={() => {
                          // Local preview is read-only, so this intentionally does nothing.
                        }}
                        turnOnTimeLabel={`Turn On Time (${getTimeZoneLabel()})`}
                        turnOffTimeLabel={`Turn Off Time (${getTimeZoneLabel()})`}
                        turnOnScheduledTime={previewPipeline.turnOnScheduledTime ?? ""}
                        turnOffScheduledTime={previewPipeline.turnOffScheduledTime ?? ""}
                        onTurnOnScheduledTimeChange={() => {
                          // Local preview is read-only, so this intentionally does nothing.
                        }}
                        onTurnOffScheduledTimeChange={() => {
                          // Local preview is read-only, so this intentionally does nothing.
                        }}
                        submitContent="Apply Schedule"
                        submitButtonType="button"
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {pipelines.map((pipeline) => (
              <div key={pipeline.rowKey} className="space-y-2">
                <ResourceContextPanel
                  resourceName={getResourceName(pipeline, product)}
                  status={pipeline.isOnline ? "Online" : "Offline"}
                  portalUrl={buildAzureResourceGroupUrl(pipeline)}
                  resourceLink={buildAzureDevOpsPipelineUrl(pipeline) ? {
                    heading: "Azure DevOps",
                    label: `Open pipeline (ID: ${pipeline.pipelineId})`,
                    url: buildAzureDevOpsPipelineUrl(pipeline) as string,
                  } : undefined}
                />
                <PipelineActions
                  pipeline={pipeline}
                  loadingAction={loadingPipelines[pipeline.rowKey] ?? null}
                  turnOff={turnoff}
                  runPipeline={runPipeline}
                  updateSchedule={updateSchedule}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <ActivityLogTable
        title="Activity Log"
        description="Tracks background execution status for turn on/off actions and keeps schedule update history in the same view."
        items={activityLogRows}
        loading={activityLogsLoading}
        page={activityLogsPage}
        totalPages={activityLogsTotalPages}
        totalCount={activityLogsTotalCount}
        onRefresh={() => fetchActivityLogs(activityLogsPage)}
        onPageChange={(pageNumber) => fetchActivityLogs(pageNumber)}
      />
      </div>
    </div>
  );
};

const productLabelFromKey = (product: string) =>
  product === "gradexpert"
    ? "GradeXpert"
    : product === "bpos"
      ? "BPOS"
      : product.charAt(0).toUpperCase() + product.slice(1).toLowerCase();

export default PowerOnOff;
